import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Parse, { ParseClasses } from "../../api/config";

export const JobApplicationForm = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [currentSection, setCurrentSection] = useState(0);

    // Add useEffect to handle ResizeObserver errors
    useEffect(() => {
        // Suppress ResizeObserver errors
        const resizeObserverErr = window.console.error;
        window.console.error = (...args) => {
            if (args[0]?.includes?.('ResizeObserver loop completed with undelivered notifications') ||
                args[0]?.includes?.('ResizeObserver loop limit exceeded')) {
                return;
            }
            resizeObserverErr(...args);
        };

        return () => {
            window.console.error = resizeObserverErr;
        };
    }, []);

    const [formData, setFormData] = useState({
        // Personal Information
        name: '',
        date: new Date().toISOString().split('T')[0],
        address: '',
        email: '',
        telephone: '',
        positionAppliedFor: '',
        payExpected: '',
        under18: '',
        canFurnishWorkPermit: '',
        appliedBefore: false,
        appliedBeforeDate: '',
        employedBefore: false,
        employedBeforeFrom: '',
        employedBeforeTo: '',
        friendsOrRelativesAtBethel: '',
        legallyEligible: '',
        currentlyEmployed: '',
        mayContactCurrentEmployer: '',
        dateAvailable: '',
        willWorkOvertime: '',
        everBonded: '',
        bondedEmployers: '',

        // Employment History (4 employers)
        employers: [
            {
                employer: '',
                phone: '',
                address: '',
                datesFrom: '',
                datesTo: '',
                supervisor: '',
                jobTitle: '',
                workPerformed: '',
                reasonForLeaving: ''
            },
            {
                employer: '',
                phone: '',
                address: '',
                datesFrom: '',
                datesTo: '',
                supervisor: '',
                jobTitle: '',
                workPerformed: '',
                reasonForLeaving: ''
            },
            {
                employer: '',
                phone: '',
                address: '',
                datesFrom: '',
                datesTo: '',
                supervisor: '',
                jobTitle: '',
                workPerformed: '',
                reasonForLeaving: ''
            },
            {
                employer: '',
                phone: '',
                address: '',
                datesFrom: '',
                datesTo: '',
                supervisor: '',
                jobTitle: '',
                workPerformed: '',
                reasonForLeaving: ''
            }
        ],
        employmentComments: '',

        // Spiritual Life
        dateAndPlaceOfNewBirth: '',
        churchAttending: '',
        denomination: '',
        pastorsName: '',
        churchInvolvement: '',
        baptismHolySpirit: '',

        // Education
        education: {
            highSchool: {
                name: '',
                location: '',
                major: '',
                yearsCompleted: '',
                diplomaDegree: ''
            },
            tradeSchool: {
                name: '',
                location: '',
                major: '',
                yearsCompleted: '',
                diplomaDegree: ''
            },
            college: {
                name: '',
                location: '',
                major: '',
                yearsCompleted: '',
                diplomaDegree: ''
            },
            graduateSchool: {
                name: '',
                location: '',
                major: '',
                yearsCompleted: '',
                diplomaDegree: ''
            }
        },

        // References (4 references)
        references: [
            {
                name: '',
                street: '',
                city: '',
                state: '',
                zip: '',
                email: '',
                telephone: ''
            },
            {
                name: '',
                street: '',
                city: '',
                state: '',
                zip: '',
                email: '',
                telephone: ''
            },
            {
                name: '',
                street: '',
                city: '',
                state: '',
                zip: '',
                email: '',
                telephone: ''
            },
            {
                name: '',
                street: '',
                city: '',
                state: '',
                zip: '',
                email: '',
                telephone: ''
            }
        ],

        // Professional Associations
        professionalAssociations: [
            { organization: '', officeHeld: '' },
            { organization: '', officeHeld: '' },
            { organization: '', officeHeld: '' },
            { organization: '', officeHeld: '' }
        ],

        // Certification
        certificationAgreed: false,
        signatureDate: new Date().toISOString().split('T')[0]
    });

    const sections = [
        'Personal Information',
        'Employment History',
        'Spiritual Life',
        'Education',
        'References',
        'Professional Associations',
        'Certification'
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name.includes('.')) {
            const keys = name.split('.');
            setFormData(prev => {
                const newData = { ...prev };
                let current = newData;
                for (let i = 0; i < keys.length - 1; i++) {
                    current = current[keys[i]];
                }
                current[keys[keys.length - 1]] = type === 'checkbox' ? checked : value;
                return newData;
            });
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            }));
        }
    };

    const handleEmployerChange = (index, field, value) => {
        setFormData(prev => {
            const newEmployers = [...prev.employers];
            newEmployers[index] = {
                ...newEmployers[index],
                [field]: value
            };
            return { ...prev, employers: newEmployers };
        });
    };

    const handleReferenceChange = (index, field, value) => {
        setFormData(prev => {
            const newReferences = [...prev.references];
            newReferences[index] = {
                ...newReferences[index],
                [field]: value
            };
            return { ...prev, references: newReferences };
        });
    };

    const handleAssociationChange = (index, field, value) => {
        setFormData(prev => {
            const newAssociations = [...prev.professionalAssociations];
            newAssociations[index] = {
                ...newAssociations[index],
                [field]: value
            };
            return { ...prev, professionalAssociations: newAssociations };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.certificationAgreed) {
            alert('Please agree to the certification to submit your application.');
            return;
        }

        setLoading(true);

        try {
            const JobApplication = Parse.Object.extend(ParseClasses.JobApplication);
            const application = new JobApplication();

            // Set all form data fields
            application.set('personalInfo', {
                name: formData.name,
                date: formData.date,
                address: formData.address,
                email: formData.email,
                telephone: formData.telephone,
                positionAppliedFor: formData.positionAppliedFor,
                payExpected: formData.payExpected,
                under18: formData.under18,
                canFurnishWorkPermit: formData.canFurnishWorkPermit,
                appliedBefore: formData.appliedBefore,
                appliedBeforeDate: formData.appliedBeforeDate,
                employedBefore: formData.employedBefore,
                employedBeforeFrom: formData.employedBeforeFrom,
                employedBeforeTo: formData.employedBeforeTo,
                friendsOrRelativesAtBethel: formData.friendsOrRelativesAtBethel,
                legallyEligible: formData.legallyEligible,
                currentlyEmployed: formData.currentlyEmployed,
                mayContactCurrentEmployer: formData.mayContactCurrentEmployer,
                dateAvailable: formData.dateAvailable,
                willWorkOvertime: formData.willWorkOvertime,
                everBonded: formData.everBonded,
                bondedEmployers: formData.bondedEmployers
            });

            application.set('employers', formData.employers);
            application.set('employmentComments', formData.employmentComments);

            application.set('spiritualLife', {
                dateAndPlaceOfNewBirth: formData.dateAndPlaceOfNewBirth,
                churchAttending: formData.churchAttending,
                denomination: formData.denomination,
                pastorsName: formData.pastorsName,
                churchInvolvement: formData.churchInvolvement,
                baptismHolySpirit: formData.baptismHolySpirit
            });

            application.set('education', formData.education);
            application.set('references', formData.references);
            application.set('professionalAssociations', formData.professionalAssociations);
            application.set('certificationAgreed', formData.certificationAgreed);
            application.set('signatureDate', formData.signatureDate);
            application.set('submittedAt', new Date().toISOString());
            application.set('status', 'pending');

            await application.save();

            alert('Your application has been submitted successfully!');
            navigate('/');
        } catch (error) {
            console.error('Error submitting application:', error);
            alert('There was an error submitting your application. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const nextSection = () => {
        if (currentSection < sections.length - 1) {
            setCurrentSection(currentSection + 1);
        }
    };

    const prevSection = () => {
        if (currentSection > 0) {
            setCurrentSection(currentSection - 1);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold text-gray-900">Bethel Christian Center</h1>
                        <p className="mt-2 text-gray-600">Application for Employment</p>
                        <p className="text-sm text-gray-500 mt-1">
                            2425 Van Buren Blvd. Riverside CA 92503 • (951) 359-1123 • BethelChristianCenter.com
                        </p>
                    </div>
                </div>

                {/* Progress Bar */}
                <div className="bg-white shadow-sm rounded-lg p-4 mb-6">
                    <div className="flex justify-between">
                        {sections.map((section, index) => (
                            <div
                                key={index}
                                className={`flex-1 text-center py-2 text-xs ${
                                    index === currentSection
                                        ? 'text-blue-600 font-semibold'
                                        : index < currentSection
                                        ? 'text-green-600'
                                        : 'text-gray-400'
                                }`}
                            >
                                <div className={`w-8 h-8 mx-auto rounded-full flex items-center justify-center ${
                                    index === currentSection
                                        ? 'bg-blue-600 text-white'
                                        : index < currentSection
                                        ? 'bg-green-600 text-white'
                                        : 'bg-gray-200'
                                }`}>
                                    {index + 1}
                                </div>
                                <div className="mt-2 hidden sm:block">{section}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Form Sections */}
                <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded-lg p-6" style={{ minHeight: '500px' }}>
                    {/* Personal Information Section */}
                    {currentSection === 0 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                Personal Information
                            </h2>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date
                                    </label>
                                    <input
                                        type="date"
                                        name="date"
                                        value={formData.date}
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Address *
                                </label>
                                <input
                                    type="text"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleChange}
                                    required
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Telephone *
                                    </label>
                                    <input
                                        type="tel"
                                        name="telephone"
                                        value={formData.telephone}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Position Applied For *
                                    </label>
                                    <input
                                        type="text"
                                        name="positionAppliedFor"
                                        value={formData.positionAppliedFor}
                                        onChange={handleChange}
                                        required
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Pay Expected
                                    </label>
                                    <input
                                        type="text"
                                        name="payExpected"
                                        value={formData.payExpected}
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-700">
                                        If under 18 yrs., can you furnish a work permit?
                                    </span>
                                    <div className="flex space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="canFurnishWorkPermit"
                                                value="N/A"
                                                checked={formData.canFurnishWorkPermit === 'N/A'}
                                                onChange={handleChange}
                                                className="mr-2"
                                            />
                                            N/A
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="canFurnishWorkPermit"
                                                value="Yes"
                                                checked={formData.canFurnishWorkPermit === 'Yes'}
                                                onChange={handleChange}
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="canFurnishWorkPermit"
                                                value="No"
                                                checked={formData.canFurnishWorkPermit === 'No'}
                                                onChange={handleChange}
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-700">
                                        Have you ever applied for employment here before?
                                    </span>
                                    <div className="flex space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="appliedBefore"
                                                value="true"
                                                checked={formData.appliedBefore === true || formData.appliedBefore === 'true'}
                                                onChange={(e) => setFormData({...formData, appliedBefore: true})}
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="appliedBefore"
                                                value="false"
                                                checked={formData.appliedBefore === false || formData.appliedBefore === 'false'}
                                                onChange={(e) => setFormData({...formData, appliedBefore: false})}
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>

                                {formData.appliedBefore && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            If yes, month and year
                                        </label>
                                        <input
                                            type="text"
                                            name="appliedBeforeDate"
                                            value={formData.appliedBeforeDate}
                                            onChange={handleChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                )}

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-700">
                                        Are you legally eligible for employment in the United States? *
                                    </span>
                                    <div className="flex space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="legallyEligible"
                                                value="Yes"
                                                checked={formData.legallyEligible === 'Yes'}
                                                onChange={handleChange}
                                                required
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="legallyEligible"
                                                value="No"
                                                checked={formData.legallyEligible === 'No'}
                                                onChange={handleChange}
                                                required
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>

                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-gray-700">
                                        Are you currently employed?
                                    </span>
                                    <div className="flex space-x-4">
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="currentlyEmployed"
                                                value="Yes"
                                                checked={formData.currentlyEmployed === 'Yes'}
                                                onChange={handleChange}
                                                className="mr-2"
                                            />
                                            Yes
                                        </label>
                                        <label className="flex items-center">
                                            <input
                                                type="radio"
                                                name="currentlyEmployed"
                                                value="No"
                                                checked={formData.currentlyEmployed === 'No'}
                                                onChange={handleChange}
                                                className="mr-2"
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date available for work
                                    </label>
                                    <input
                                        type="date"
                                        name="dateAvailable"
                                        value={formData.dateAvailable}
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Employment History Section */}
                    {currentSection === 1 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                Employment History
                            </h2>
                            <p className="text-sm text-gray-600">
                                List your last four (4) employers, starting with the most recent (including military experience).
                            </p>

                            {formData.employers.map((employer, index) => (
                                <div key={index} className="border rounded-lg p-4 space-y-4">
                                    <h3 className="font-medium text-gray-900">Employer #{index + 1}</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Employer
                                            </label>
                                            <input
                                                type="text"
                                                value={employer.employer}
                                                onChange={(e) => handleEmployerChange(index, 'employer', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Phone
                                            </label>
                                            <input
                                                type="tel"
                                                value={employer.phone}
                                                onChange={(e) => handleEmployerChange(index, 'phone', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Address
                                        </label>
                                        <input
                                            type="text"
                                            value={employer.address}
                                            onChange={(e) => handleEmployerChange(index, 'address', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                From Date
                                            </label>
                                            <input
                                                type="date"
                                                value={employer.datesFrom}
                                                onChange={(e) => handleEmployerChange(index, 'datesFrom', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                To Date
                                            </label>
                                            <input
                                                type="date"
                                                value={employer.datesTo}
                                                onChange={(e) => handleEmployerChange(index, 'datesTo', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Supervisor
                                            </label>
                                            <input
                                                type="text"
                                                value={employer.supervisor}
                                                onChange={(e) => handleEmployerChange(index, 'supervisor', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Job Title and Work Performed
                                        </label>
                                        <textarea
                                            value={employer.workPerformed}
                                            onChange={(e) => handleEmployerChange(index, 'workPerformed', e.target.value)}
                                            rows="3"
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Reason for Leaving
                                        </label>
                                        <input
                                            type="text"
                                            value={employer.reasonForLeaving}
                                            onChange={(e) => handleEmployerChange(index, 'reasonForLeaving', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            ))}

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Comments (Please explain any gaps in employment)
                                </label>
                                <textarea
                                    name="employmentComments"
                                    value={formData.employmentComments}
                                    onChange={handleChange}
                                    rows="3"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                    )}

                    {/* Spiritual Life Section */}
                    {currentSection === 2 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                Spiritual Life
                            </h2>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Date and Place of New Birth
                                </label>
                                <input
                                    type="text"
                                    name="dateAndPlaceOfNewBirth"
                                    value={formData.dateAndPlaceOfNewBirth}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Church Now Attending
                                    </label>
                                    <input
                                        type="text"
                                        name="churchAttending"
                                        value={formData.churchAttending}
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Denomination
                                    </label>
                                    <input
                                        type="text"
                                        name="denomination"
                                        value={formData.denomination}
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    Pastor's Name
                                </label>
                                <input
                                    type="text"
                                    name="pastorsName"
                                    value={formData.pastorsName}
                                    onChange={handleChange}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    What is your involvement in your church?
                                </label>
                                <textarea
                                    name="churchInvolvement"
                                    value={formData.churchInvolvement}
                                    onChange={handleChange}
                                    rows="3"
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>

                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-700">
                                    Have you received the Baptism of the Holy Spirit with the evidence of speaking in tongues?
                                </span>
                                <div className="flex space-x-4">
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="baptismHolySpirit"
                                            value="Yes"
                                            checked={formData.baptismHolySpirit === 'Yes'}
                                            onChange={handleChange}
                                            className="mr-2"
                                        />
                                        Yes
                                    </label>
                                    <label className="flex items-center">
                                        <input
                                            type="radio"
                                            name="baptismHolySpirit"
                                            value="No"
                                            checked={formData.baptismHolySpirit === 'No'}
                                            onChange={handleChange}
                                            className="mr-2"
                                        />
                                        No
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Education Section */}
                    {currentSection === 3 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                Education
                            </h2>
                            <p className="text-sm text-gray-600">
                                Please list the last three (3) schools you've attended
                            </p>

                            {['highSchool', 'tradeSchool', 'college', 'graduateSchool'].map((level) => (
                                <div key={level} className="border rounded-lg p-4 space-y-4">
                                    <h3 className="font-medium text-gray-900">
                                        {level === 'highSchool' && 'High School'}
                                        {level === 'tradeSchool' && 'Business/Trade School'}
                                        {level === 'college' && 'College/University'}
                                        {level === 'graduateSchool' && 'Graduate School'}
                                    </h3>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                School Name and Location
                                            </label>
                                            <input
                                                type="text"
                                                name={`education.${level}.name`}
                                                value={formData.education[level].name}
                                                onChange={handleChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Major
                                            </label>
                                            <input
                                                type="text"
                                                name={`education.${level}.major`}
                                                value={formData.education[level].major}
                                                onChange={handleChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Years Completed
                                            </label>
                                            <input
                                                type="text"
                                                name={`education.${level}.yearsCompleted`}
                                                value={formData.education[level].yearsCompleted}
                                                onChange={handleChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Diploma or Degree
                                            </label>
                                            <input
                                                type="text"
                                                name={`education.${level}.diplomaDegree`}
                                                value={formData.education[level].diplomaDegree}
                                                onChange={handleChange}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* References Section */}
                    {currentSection === 4 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                References
                            </h2>
                            <p className="text-sm text-gray-600">
                                Please list three (3) or four (4) personal references who are not related to you and were not previous work supervisors
                            </p>

                            {formData.references.map((reference, index) => (
                                <div key={index} className="border rounded-lg p-4 space-y-4">
                                    <h3 className="font-medium text-gray-900">Reference #{index + 1}</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Name
                                        </label>
                                        <input
                                            type="text"
                                            value={reference.name}
                                            onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Street Address
                                        </label>
                                        <input
                                            type="text"
                                            value={reference.street}
                                            onChange={(e) => handleReferenceChange(index, 'street', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                City
                                            </label>
                                            <input
                                                type="text"
                                                value={reference.city}
                                                onChange={(e) => handleReferenceChange(index, 'city', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                State
                                            </label>
                                            <input
                                                type="text"
                                                value={reference.state}
                                                onChange={(e) => handleReferenceChange(index, 'state', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Zip
                                            </label>
                                            <input
                                                type="text"
                                                value={reference.zip}
                                                onChange={(e) => handleReferenceChange(index, 'zip', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Email
                                            </label>
                                            <input
                                                type="email"
                                                value={reference.email}
                                                onChange={(e) => handleReferenceChange(index, 'email', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">
                                                Telephone
                                            </label>
                                            <input
                                                type="tel"
                                                value={reference.telephone}
                                                onChange={(e) => handleReferenceChange(index, 'telephone', e.target.value)}
                                                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Professional Associations Section */}
                    {currentSection === 5 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                Professional, Trade, Business or Civic Associations
                            </h2>
                            <p className="text-sm text-gray-600">
                                Please exclude those which may disclose your sex, race, color, age, national origin, disability or other protected status
                            </p>

                            {formData.professionalAssociations.map((association, index) => (
                                <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Organization
                                        </label>
                                        <input
                                            type="text"
                                            value={association.organization}
                                            onChange={(e) => handleAssociationChange(index, 'organization', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">
                                            Office(s) Held
                                        </label>
                                        <input
                                            type="text"
                                            value={association.officeHeld}
                                            onChange={(e) => handleAssociationChange(index, 'officeHeld', e.target.value)}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Certification Section */}
                    {currentSection === 6 && (
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">
                                Certification
                            </h2>

                            <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                <p className="text-sm text-gray-700">
                                    I certify that the answers I have provided are true and complete to the best of my knowledge. I
                                    give Bethel the right to investigate all statements contained in this application and to secure
                                    additional information about me if job related. I hereby release from liability Bethel Christian Center
                                    and its representatives for seeking such information and all other persons, corporations, or
                                    organizations for providing such information.
                                </p>

                                <p className="text-sm text-gray-700">
                                    I understand and acknowledge that any employment relationship with Bethel Christian Center is
                                    "at will," which means that I am free to resign at any time and Bethel Christian Center may
                                    terminate my employment at any time with or without cause. It is further understood that no
                                    representative of Bethel Christian Center has the authority to make any assurances to the contrary.
                                </p>

                                <p className="text-sm text-gray-700">
                                    Further, I understand that the California State Law requires all employees who work with or have
                                    direct contact with minor children must submit to a fingerprint check by the State and the Federal
                                    Bureau of Investigation. When appropriate, two sets of fingerprints must be recorded by a member
                                    of a California State Authority for this purpose and must be submitted to Bethel's Personnel Office as
                                    a condition of employment.
                                </p>

                                <p className="text-sm text-gray-700 font-semibold">
                                    Bethel Christian Center considers applicants without regard to race, color, sex,
                                    national origin, age, marital or veteran status, non-job-related medical condition or
                                    disability, or any other legally protected status.
                                </p>

                                <p className="text-sm text-gray-700">
                                    This application will be considered active for forty-five (45) days. Applicants desiring to be
                                    considered for employment beyond this time period will be required to fill out a new application.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <label className="flex items-start">
                                    <input
                                        type="checkbox"
                                        name="certificationAgreed"
                                        checked={formData.certificationAgreed}
                                        onChange={handleChange}
                                        className="mt-1 mr-3"
                                    />
                                    <span className="text-sm text-gray-700">
                                        I have read and agree to the certification statement above. By checking this box and submitting
                                        this form, I am electronically signing this application.
                                    </span>
                                </label>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">
                                        Date of Signature
                                    </label>
                                    <input
                                        type="date"
                                        name="signatureDate"
                                        value={formData.signatureDate}
                                        onChange={handleChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Navigation Buttons */}
                    <div className="mt-8 flex justify-between">
                        {currentSection > 0 && (
                            <button
                                type="button"
                                onClick={prevSection}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Previous
                            </button>
                        )}

                        <div className="ml-auto">
                            {currentSection < sections.length - 1 ? (
                                <button
                                    type="button"
                                    onClick={nextSection}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    Next
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    disabled={loading || !formData.certificationAgreed}
                                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                                >
                                    {loading ? 'Submitting...' : 'Submit Application'}
                                </button>
                            )}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};